// Lost/stolen responses (AWS-PORT chunk 7; v2 per EPOCH-KEYPAIR.md).
//
// TWO flows now, because a v2 uploader (Windows) never holds a secret:
//
//   retireDevices — a LOST OR RETIRED MONITORED DEVICE. There is no separate
//     "lost device" action (2026-09-19): the device is deleted on the Devices
//     page, which is what locks it out, and this re-signs the chain-committed
//     device list to match - the deleted device's entry is tombstoned (its
//     signing key stops being a current author, its earlier screenshots still
//     open) and dropped for good once nothing it authored can still exist
//     (DeviceRetirement.js). No identity rotation, no epoch bump, no purge, no
//     recovery-code void: the device held only its device signing key.
//
//   rotateIdentity — a COMPROMISED BROWSER / IDENTITY (a keystore holding the
//     identity secrets and every epoch secret was exposed). Port of dev-cli
//     `rotate` with the AWS write ordering — every write is validated
//     against §12 chain custody, so the CROSS-SIGNED identity entry must
//     land FIRST (custody flips old→new), then everything else is signed by
//     the NEW key:
//       1. chain: identity entry (old key authorizes, new key proves possession)
//       2. identityBundle: new keys + FRESH wraps (recovery reissued — the old
//          printed code is a total-access credential and this is a compromise
//          response; this browser's passkey optionally re-wrapped via one PRF
//          touch — other browsers' passkeys die and those browsers re-link)
//       3. memberList: owner entry → new keys, epoch bumped, NEW epochPk
//          published (chain-committed) — uploaders pick the new key up on
//          their next refresh straight from this document
//       4. deviceList: ALWAYS re-signed under the new key (chain-committed) —
//          every reader (uploader refresh, Decryptor, the service's own
//          validator) checks the list's signature against CURRENT custody,
//          so a list still signed by the retired key is refused everywhere;
//          any device the caller also wants locked out is tombstoned here
//       5. adkEnvelopes: NEW epoch secret only, sealed per member — the
//          replace-semantics write withdraws every old-epoch row
//          (delete-on-rotate)
//     There are no deliveries: uploaders learn the new custody key from the
//     chain and the new epoch key from the member list.
//   Then (separate, explicit) `purgeHistory` deletes the pre-rotation records
//   the exposed epoch secrets could open.

import E2eeApi from '@/aws/e2ee/E2eeApi'
import E2EEPasscodePrompt from '../E2EEPasscodePrompt'
import { getSodium } from './sodium'
import { loadIdentity, saveIdentity, clearIdentity, getLocalPasskey } from './Keystore'
import { Nc1UnlockRequiredError } from '../E2EEErrors'
import { invalidateContext } from './Decryptor'
import { evaluatePrf } from './PasskeyPrf'
import { wrapSecrets, wrapToWebauthn } from './shared/kek.js'
import { generateRecoveryCode, recoveryCodeDisplay } from './shared/recovery.js'
import {
  b64, ub64, epochKeypair, deviceListSignedBytes, memberListSignedBytes,
} from './shared/envelope.js'
import { canonicalizeBytes } from './shared/jcs.js'
import {
  KIND_IDENTITY, KIND_DEVICE_LIST, KIND_MEMBER_LIST,
  docHashIdentity, docHashDeviceList, docHashMemberList,
  signEntry, entryHash, verifyChain, assertDocCommitted,
} from './shared/translog.js'
import { planEnvelopeWrite } from './EpochRetention'
import { planDeviceRetirement, applyDeviceRetirement } from './DeviceRetirement'

/** Unlocked identity + chain-verified world; throws Nc1UnlockRequiredError when stale/locked. */
async function openForCustodyWrite (email) {
  const sodium = await getSodium()
  const cached = await loadIdentity(email)
  if (!cached || !cached.secrets) throw new Nc1UnlockRequiredError(email)
  const accountId = cached.altAccountId
  const deviceList = (await E2eeApi.getDeviceList(email)).data
  const memberList = (await E2eeApi.getMemberList(email)).data
  const log = (await E2eeApi.getLog(email)).data
  const chain = verifyChain(sodium, accountId, log, null)
  if (chain.head.signerPk !== cached.secrets.id_sign_pk) {
    // Already rotated elsewhere — this cached identity is stale.
    await clearIdentity(email)
    throw new Nc1UnlockRequiredError(email)
  }
  assertDocCommitted('member-list', chain.latest.memberList, memberList.version, docHashMemberList(sodium, memberList))
  assertDocCommitted('device-list', chain.latest.deviceList, deviceList.version, docHashDeviceList(sodium, deviceList))
  const owner = memberList.members.find((m) => m.role === 'owner')
  if (!owner || owner.userId !== accountId) throw new Error('Only the account owner can do this.')
  return { sodium, cached, accountId, deviceList, memberList, log, owner }
}

/**
 * Bring the signed device list in step with the account's devices in ONE
 * chain-committed re-signing (DeviceRetirement.js has the rules): tombstone
 * `deletedDeviceIds` (device UUIDs just deleted) and - only when the caller
 * passes the COMPLETE, freshly fetched `registeredDeviceIds` - any settled
 * entry whose device is no longer registered; drop tombstones past their
 * retention. Returns `{ tombstoned, pruned, listVersion }`; nothing to do is
 * a no-op without a write. Throws Nc1UnlockRequiredError on a locked browser.
 */
export async function retireDevices ({ email, deletedDeviceIds, registeredDeviceIds }) {
  email = String(email).toLowerCase()
  const { sodium, cached, accountId, deviceList, log } = await openForCustodyWrite(email)
  const nowMs = Date.now()
  // Planned against the chain-verified list, never the caller's copy.
  const plan = planDeviceRetirement(deviceList, { deletedDeviceIds, registeredDeviceIds, nowMs })
  if (!plan.tombstone.length && !plan.prune.length) {
    return { tombstoned: [], pruned: [], listVersion: deviceList.version }
  }

  const idSignSk = ub64(sodium, cached.secrets.id_sign_sk)
  applyDeviceRetirement(deviceList, plan, nowMs)
  deviceList.version += 1
  deviceList.identitySig = b64(sodium, sodium.crypto_sign_detached(deviceListSignedBytes(deviceList), idSignSk))
  const entry = signEntry(sodium, {
    accountId, seq: log.length, prevHash: entryHash(sodium, log[log.length - 1]), ts: nowMs,
    kind: KIND_DEVICE_LIST, docVersion: deviceList.version,
    docHash: docHashDeviceList(sodium, deviceList), signerPk: cached.secrets.id_sign_pk,
  }, idSignSk)
  await E2eeApi.putDeviceList(email, deviceList, entry)
  invalidateContext(email)
  return { tombstoned: plan.tombstone, pruned: plan.prune, listVersion: deviceList.version }
}

export async function rotateIdentity ({ email, lostDeviceId, lostDeviceIds }) {
  email = String(email).toLowerCase()
  const { sodium, cached, accountId, deviceList, memberList, log, owner } = await openForCustodyWrite(email)
  const old = cached.secrets
  const bundle = (await E2eeApi.getIdentityBundle(email)).data
  const envelopes = (await E2eeApi.getAdkEnvelopes(email)).data || []

  // `lostDeviceIds` (0..n): device-list entries to tombstone alongside the
  // rotation (an uploader the caller also considers lost). Zero ids (stolen
  // browser, clean list) still rotates in full; there's just nothing to
  // tombstone.
  const lostIds = new Set((lostDeviceIds || (lostDeviceId ? [lostDeviceId] : [])).filter(Boolean))
  const lostEntries = deviceList.devices.filter((d) => lostIds.has(d.deviceId) && d.revokedAt == null)

  // My epoch secrets (opened with the OLD identity) — only their epoch
  // numbers matter now; the new epoch's secret is fresh.
  const myEpochs = []
  for (const env of envelopes) {
    if (env.userId !== accountId) continue
    sodium.crypto_box_seal_open(ub64(sodium, env.sealedAdk), ub64(sodium, old.id_enc_pk), ub64(sodium, old.id_enc_sk))
    myEpochs.push(env.keyEpoch)
  }
  if (!myEpochs.length) throw new Error('No epoch secret could be unsealed — cannot rotate.')

  // ---- 0. new identity + new epoch, minted up front (no writes yet) --------
  const newEnc = sodium.crypto_box_keypair()
  const newSign = sodium.crypto_sign_keypair()
  const Vnew = (bundle.identityVersion ?? 1) + 1
  const newSecrets = {
    id_enc_pk: b64(sodium, newEnc.publicKey),
    id_enc_sk: b64(sodium, newEnc.privateKey),
    id_sign_pk: b64(sodium, newSign.publicKey),
    id_sign_sk: b64(sodium, newSign.privateKey),
    identity_version: Vnew,
  }
  const newSecretsBytes = new TextEncoder().encode(JSON.stringify(newSecrets))

  // The new epoch's secret sealed per member (the owner to the NEW identity
  // key), built BEFORE any write so the step-5 replace — which withdraws
  // every old-epoch row — is budgeted against the service's 100-item
  // transaction cap before the chain moves (2026-09-14, EpochRetention.js).
  // Withdraw-only shrink writes go out now under the OLD key, which still
  // holds custody; the final write lands under the new key in step 5.
  const newEpoch = Math.max(memberList.keyEpoch, ...myEpochs) + 1
  const epoch = epochKeypair(sodium)
  const mintedAt = Date.now()
  const newEnvelopes = memberList.members.map((m) => ({
    accountId,
    keyEpoch: newEpoch,
    userId: m.userId,
    sealedAdk: b64(sodium, sodium.crypto_box_seal(epoch.epochSk,
      m.userId === accountId ? newEnc.publicKey : ub64(sodium, m.idEncPk))),
    epochSince: mintedAt,
  }))
  const envelopePlan = planEnvelopeWrite({ existing: envelopes, updated: newEnvelopes })
  if (envelopePlan.error) throw new Error(envelopePlan.error)
  for (const value of envelopePlan.shrinkWrites) {
    await E2eeApi.putAdkEnvelopes(email, value,
      b64(sodium, sodium.crypto_sign_detached(canonicalizeBytes(value), ub64(sodium, old.id_sign_sk))))
  }

  // ---- 1. the cross-signed chain entry (custody flip) ----------------------
  let seq = log.length
  let prevHash = entryHash(sodium, log[log.length - 1])
  const pushEntry = async (core, signSk, newSignerSk = null) => {
    const entry = signEntry(sodium, { accountId, seq, prevHash, ts: Date.now(), ...core }, signSk, newSignerSk)
    await E2eeApi.appendLog(email, entry)
    prevHash = entryHash(sodium, entry)
    seq += 1
    return entry
  }

  await pushEntry({
    kind: KIND_IDENTITY,
    docVersion: Vnew,
    docHash: docHashIdentity(sodium, { idEncPk: newSecrets.id_enc_pk, idSignPk: newSecrets.id_sign_pk, identityVersion: Vnew }),
    signerPk: old.id_sign_pk,
    newSignerPk: newSecrets.id_sign_pk,
  }, ub64(sodium, old.id_sign_sk), ub64(sodium, newSecrets.id_sign_sk))

  const newSignSk = ub64(sodium, newSecrets.id_sign_sk)

  // ---- 2. bundle: new keys + fresh wraps -----------------------------------
  const recoveryCode = generateRecoveryCode(sodium)
  const wraps = { recovery: wrapSecrets(sodium, recoveryCode, newSecretsBytes) }
  // Re-wrap THIS browser's passkey when it has one (single PRF touch); other
  // passkeys wrapped the old secrets and are dropped.
  let passkeyRewrapped = false
  try {
    const local = await getLocalPasskey(email)
    const oldWebauthn = (bundle.wraps && bundle.wraps.webauthn) || []
    const mine = local && oldWebauthn.find((w) => w.credentialId === local.credentialIdB64)
    if (mine) {
      const { credentialIdB64, prfOutput } = await evaluatePrf({
        credentialIdsB64: [mine.credentialId], prfSaltB64: mine.prfSalt,
      })
      if (credentialIdB64 === mine.credentialId) {
        wraps.webauthn = [wrapToWebauthn(sodium, prfOutput, newSecretsBytes, {
          credentialId: mine.credentialId, prfSalt: mine.prfSalt,
        })]
        sodium.memzero(prfOutput)
        passkeyRewrapped = true
      }
    }
  } catch (e) {
    // Passkey re-wrap is best-effort — the reissued recovery code always ships.
    // eslint-disable-next-line no-console
    console.log('passkey re-wrap skipped', e)
  }

  const newBundle = {
    ...bundle,
    identityVersion: Vnew,
    idEncPk: newSecrets.id_enc_pk,
    idSignPk: newSecrets.id_sign_pk,
    wraps,
  }
  await E2eeApi.putIdentityBundle(email, newBundle,
    b64(sodium, sodium.crypto_sign_detached(canonicalizeBytes(newBundle), newSignSk)))

  // ---- 3. member list: owner → new keys, epoch bumped, new epochPk ---------
  owner.idEncPk = newSecrets.id_enc_pk
  owner.idSignPk = newSecrets.id_sign_pk
  memberList.keyEpoch = newEpoch
  memberList.epochPk = b64(sodium, epoch.epochPk)
  memberList.version += 1
  memberList.ownerSig = b64(sodium, sodium.crypto_sign_detached(memberListSignedBytes(memberList), newSignSk))
  {
    const entry = signEntry(sodium, {
      accountId, seq, prevHash, ts: Date.now(),
      kind: KIND_MEMBER_LIST, docVersion: memberList.version,
      docHash: docHashMemberList(sodium, memberList), signerPk: newSecrets.id_sign_pk,
    }, newSignSk)
    await E2eeApi.putMemberList(email, memberList, entry)
    prevHash = entryHash(sodium, entry)
    seq += 1
  }

  // ---- 4. device list: re-sign under the NEW key (+ tombstone lockouts) ----
  // Unconditional: with zero lost devices (the plain compromised-browser
  // case) the list would otherwise stay signed by the retired key and every
  // uploader's next refresh would refuse it ("not authentic under chain
  // custody") — found live 2026-09-11 by full-loop.ps1.
  {
    for (const entry of lostEntries) entry.revokedAt = Date.now()
    deviceList.version += 1
    deviceList.identitySig = b64(sodium,
      sodium.crypto_sign_detached(deviceListSignedBytes(deviceList), newSignSk))
    const entry = signEntry(sodium, {
      accountId, seq, prevHash, ts: Date.now(),
      kind: KIND_DEVICE_LIST, docVersion: deviceList.version,
      docHash: docHashDeviceList(sodium, deviceList), signerPk: newSecrets.id_sign_pk,
    }, newSignSk)
    await E2eeApi.putDeviceList(email, deviceList, entry)
    prevHash = entryHash(sodium, entry)
    seq += 1
  }

  // ---- 5. new epoch secret, sealed per member — replace withdraws old epochs
  // (rows minted + the write budgeted in step 0)
  await E2eeApi.putAdkEnvelopes(email, newEnvelopes,
    b64(sodium, sodium.crypto_sign_detached(canonicalizeBytes(newEnvelopes), newSignSk)))

  // ---- adopt locally --------------------------------------------------------
  await saveIdentity(email, { secrets: newSecrets, altAccountId: accountId, email })
  invalidateContext()
  E2EEPasscodePrompt.notifyUnlocked(email)

  return {
    newVersion: Vnew,
    newEpoch,
    rotatedAtMs: Date.now(),
    recoveryDisplay: recoveryCodeDisplay(recoveryCode),
    passkeyRewrapped,
    survivorCount: deviceList.devices.filter((d) => d.revokedAt == null).length,
  }
}

/**
 * OWNER-RESET.md auto re-admission (2026-09-22). Right after an owner
 * identity reset the new device list is EMPTY, and every monitored device
 * would otherwise re-post its enrollment and wait to be approved by name
 * again. But approval in v2 is nothing more than the device's permanent
 * signing key being on the owner-signed, chain-committed device list — and
 * the reset did not touch those keys. So the browser that just reset (it
 * holds the new identity) re-admits, in ONE chain-committed write, every
 * device that was live before the reset and is still registered on the
 * account; the devices adopt the new chain on their next settings tick
 * with no human step (Windows `E2EEManager.ContinueAfterResetAsync` tries
 * the list before re-posting). `devices` = `[{ deviceId, devSignPk }]`
 * snapshotted BEFORE the reset. Idempotent: entries already live on the
 * list are left alone. Returns `{ readmitted: [deviceId], listVersion }`.
 * Throws Nc1UnlockRequiredError on a locked browser.
 */
export async function readmitDevices ({ email, devices }) {
  email = String(email).toLowerCase()
  const wanted = (devices || []).filter((d) => d && d.deviceId && d.devSignPk)
  const { sodium, cached, accountId, deviceList, log } = await openForCustodyWrite(email)
  const live = new Set(deviceList.devices.filter((d) => d.revokedAt == null).map((d) => d.deviceId))
  const missing = wanted.filter((d) => !live.has(d.deviceId))
  if (!missing.length) return { readmitted: [], listVersion: deviceList.version }

  const nowMs = Date.now()
  const idSignSk = ub64(sodium, cached.secrets.id_sign_sk)
  // A fresh genesis lists nothing, but be exact: a tombstone for the same id
  // is replaced by the live entry rather than duplicated.
  const ids = new Set(missing.map((d) => d.deviceId))
  deviceList.devices = deviceList.devices.filter((d) => !ids.has(d.deviceId))
  for (const d of missing) deviceList.devices.push({ deviceId: d.deviceId, devSignPk: d.devSignPk, addedAt: nowMs })
  deviceList.version += 1
  deviceList.identitySig = b64(sodium, sodium.crypto_sign_detached(deviceListSignedBytes(deviceList), idSignSk))
  const entry = signEntry(sodium, {
    accountId, seq: log.length, prevHash: entryHash(sodium, log[log.length - 1]), ts: nowMs,
    kind: KIND_DEVICE_LIST, docVersion: deviceList.version,
    docHash: docHashDeviceList(sodium, deviceList), signerPk: cached.secrets.id_sign_pk,
  }, idSignSk)
  await E2eeApi.putDeviceList(email, deviceList, entry)
  invalidateContext(email)
  return { readmitted: missing.map((d) => d.deviceId), listVersion: deviceList.version }
}

export const PURGE_POLL_INTERVAL_MS = 2000
/** The worker function's own budget: a job still not finished by then died mid-walk. */
export const PURGE_WAIT_LIMIT_MS = 15 * 60 * 1000

/**
 * Permanently delete everything before `cutoffMs` (custody-signed).
 *
 * 2026-09-14: the service answers `202 { jobId }` and walks the records in
 * a worker function (API Gateway's hard 29 s cap made the inline walk a 504
 * for any prod-sized account), so this polls the job to its end. Resolves
 * to the job row — `{ jobId, state: 'done' | 'failed' | 'stalled',
 * removedEvents?, removedImages?, error? }` — and never throws for a job
 * that ran and failed, only for a request the service refused. Deletes are
 * time-ranged and idempotent, so a failed or stalled job is simply
 * requested again. `onProgress(job)` is called on every poll.
 */
export async function purgeHistory ({ email, cutoffMs, onProgress }) {
  email = String(email).toLowerCase()
  const sodium = await getSodium()
  const cached = await loadIdentity(email)
  if (!cached || !cached.secrets) throw new Nc1UnlockRequiredError(email)
  const sig = b64(sodium, sodium.crypto_sign_detached(
    canonicalizeBytes({ accountId: cached.altAccountId, keepFromMs: cutoffMs }),
    ub64(sodium, cached.secrets.id_sign_sk)))
  const res = await E2eeApi.purgeRecords(email, cutoffMs, sig)
  const body = res.data || {}
  // A pre-async service (200 with the counts inline) is still a finished purge.
  if (!body.jobId) return { state: 'done', ...body }
  return waitForPurge({ email, jobId: body.jobId, onProgress })
}

/** Poll one purge job until it is done or failed, or the worker's budget has passed ('stalled'). */
export async function waitForPurge ({
  email, jobId, onProgress, limitMs = PURGE_WAIT_LIMIT_MS, intervalMs = PURGE_POLL_INTERVAL_MS,
}) {
  email = String(email).toLowerCase()
  const deadline = Date.now() + limitMs
  let job = { jobId, state: 'queued' }
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
    job = (await E2eeApi.getPurgeStatus(email, jobId)).data || job
    if (job.state === 'done' || job.state === 'failed') return job
    if (onProgress) onProgress(job)
  }
  return { ...job, state: 'stalled' }
}
